/**
 * 视频剪辑弹窗编辑器。
 *
 * 从视频组的某个片段打开，支持三种操作：
 *  - trim（裁剪）：选 in/out 点，截取区间
 *  - split（分割）：在分割点切成两段
 *  - speed（变速）：0.5x / 0.75x / 1.5x / 2x
 *
 * 处理完成后在画布上生成新的视频节点（trim/speed 1 个，split 2 个）。
 * 也可直接从编辑器移除该片段（作用于视频组本身）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Scissors, Split, Gauge, Trash2, X, Loader2, Download } from 'lucide-react';
import type { CanvasNode } from '../types';
import { boardActions } from '../state/boardStore';
import { makeNode } from '../utils';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { toPersistableMediaUrl } from '../utils/persistableMediaUrl';
import { canvasAPI, type VideoEditJobView } from '../services/canvasAPI';
import { removeVideoFromGroup } from '../generators/videoGroupOps';
import toast from '../../../components/Toast';

type EditMode = 'trim' | 'split' | 'speed';

interface VideoClipEditorProps {
  /** 源节点：视频组节点或单视频节点 */
  sourceNode: CanvasNode;
  /** 片段在视频组中的索引；单视频节点为 undefined */
  clipIndex?: number;
  /** 视频地址（已 resolveBrowserMediaUrl） */
  videoUrl: string;
  /** 视频时长（秒），可能为 null */
  durationSec: number | null;
  onClose: () => void;
}

const SPEED_OPTIONS = [0.5, 0.75, 1.5, 2];

function formatSec(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = (sec % 60).toFixed(1);
  return `${m}:${s.padStart(4, '0')}`;
}

function stageLabel(stage?: string | null): string {
  switch (stage) {
    case 'downloading':
      return '下载源视频';
    case 'processing':
      return '处理中';
    case 'uploading':
      return '上传结果';
    default:
      return '处理中';
  }
}

export function VideoClipEditor({
  sourceNode,
  clipIndex,
  videoUrl,
  durationSec,
  onClose,
}: VideoClipEditorProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  const [mode, setMode] = useState<EditMode>('trim');
  const [duration, setDuration] = useState(durationSec ?? 0);
  const [currentTime, setCurrentTime] = useState(0);

  // trim: in/out 点（秒）
  const [trimIn, setTrimIn] = useState(0);
  const [trimOut, setTrimOut] = useState(durationSec ?? 0);

  // split: 分割点
  const [splitAt, setSplitAt] = useState((durationSec ?? 0) / 2);

  // speed: 选中的倍率
  const [speed, setSpeed] = useState(1.5);

  // 拖拽状态
  const [dragging, setDragging] = useState<'in' | 'out' | 'split' | null>(null);

  // 任务状态
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<VideoEditJobView | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 视频元数据加载后初始化
  const handleLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const d = v.duration;
    if (Number.isFinite(d) && d > 0) {
      setDuration(d);
      setTrimOut(d);
      setSplitAt(d / 2);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (v) setCurrentTime(v.currentTime);
  }, []);

  // 时间轴百分比
  const pct = (t: number) => (duration > 0 ? (t / duration) * 100 : 0);

  // 点击/拖拽时间轴
  const timeFromEvent = (clientX: number): number => {
    const el = timelineRef.current;
    if (!el || duration <= 0) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  };

  const handleTimelineMouseDown = (e: React.MouseEvent) => {
    const t = timeFromEvent(e.clientX);
    const v = videoRef.current;
    if (v) v.currentTime = t;
  };

  // 手柄拖拽
  useEffect(() => {
    if (!dragging) return;

    const onMove = (e: MouseEvent) => {
      const t = timeFromEvent(e.clientX);
      if (dragging === 'in') {
        setTrimIn(Math.max(0, Math.min(t, trimOut - 0.1)));
      } else if (dragging === 'out') {
        setTrimOut(Math.min(duration, Math.max(t, trimIn + 0.1)));
      } else if (dragging === 'split') {
        setSplitAt(Math.max(0.1, Math.min(duration - 0.1, t)));
      }
    };
    const onUp = () => setDragging(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, duration, trimIn, trimOut]);

  // 轮询任务
  useEffect(() => {
    if (!jobId) return;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      try {
        const job = await canvasAPI.getVideoEditJob(jobId);
        if (stopped) return;
        setJobStatus(job);

        if (job.status === 'completed') {
          // 创建新视频节点
          const baseUrl = toPersistableMediaUrl(job.url);
          if (baseUrl) {
            const label =
              mode === 'trim'
                ? `（裁剪 ${formatSec(trimIn)}-${formatSec(trimOut)}）`
                : mode === 'speed'
                  ? `（变速 ${speed}x）`
                  : '';
            boardActions.addNode(
              makeNode(
                'video',
                { x: sourceNode.x + sourceNode.width + 60, y: sourceNode.y },
                {
                  videoUrl: baseUrl,
                  coverUrl: null,
                  status: 'completed',
                  videoDurationSec: job.durationSec ?? undefined,
                  modelLabel: '视频剪辑',
                  prompt: label,
                }
              )
            );
          }

          if (mode === 'split' && job.url2) {
            const url2 = toPersistableMediaUrl(job.url2);
            if (url2) {
              boardActions.addNode(
                makeNode(
                  'video',
                  { x: sourceNode.x + sourceNode.width + 60, y: sourceNode.y + 80 },
                  {
                    videoUrl: url2,
                    coverUrl: null,
                    status: 'completed',
                    videoDurationSec: job.durationSec2 ?? undefined,
                    modelLabel: '视频剪辑',
                    prompt: `（分割 ${formatSec(splitAt)} 处 - 后段）`,
                  }
                )
              );
            }
          }

          toast('视频剪辑完成');
          onClose();
          return;
        }

        if (job.status === 'failed') {
          toast(job.errorMessage || '视频剪辑失败');
          setJobId(null);
          setSubmitting(false);
          return;
        }
      } catch {
        // 网络错误：下次重试
      }
    };

    tick();
    const interval = setInterval(tick, 3000);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [jobId, mode, trimIn, trimOut, speed, splitAt, sourceNode, onClose]);

  const handleSubmit = async () => {
    if (submitting || jobId) return;
    setSubmitting(true);
    setJobStatus(null);

    try {
      const params: Record<string, number> = {};
      if (mode === 'trim') {
        params.start = trimIn;
        params.end = trimOut;
      } else if (mode === 'split') {
        params.splitAt = splitAt;
      } else {
        params.speed = speed;
      }

      // 视频跳到起始位置预览
      if (mode === 'trim' && videoRef.current) {
        videoRef.current.currentTime = trimIn;
      }

      const result = await canvasAPI.submitVideoEdit({
        inputUrl: videoUrl,
        operation: mode,
        params,
      });
      setJobId(result.jobId);
    } catch (err) {
      toast(err instanceof Error ? err.message : '提交剪辑任务失败');
      setSubmitting(false);
    }
  };

  const handleRemove = () => {
    if (clipIndex === undefined) return;
    removeVideoFromGroup(sourceNode.id, clipIndex);
    toast('已从视频组移除');
    onClose();
  };

  const isProcessing = !!jobId && jobStatus?.status !== 'failed';
  const progress = jobStatus?.progress ?? 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-[640px] max-w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="flex items-center gap-2">
            <Scissors className="h-4 w-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-800">视频剪辑</span>
            <span className="text-xs text-slate-400">
              {clipIndex !== undefined ? `第 ${clipIndex + 1} 段 · ` : ''}{formatSec(duration)}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 视频预览 */}
        <div className="bg-slate-900">
          <video
            ref={videoRef}
            src={resolveBrowserMediaUrl(videoUrl) || videoUrl}
            controls
            className="max-h-[320px] w-full object-contain"
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
          />
        </div>

        {/* 时间轴 */}
        <div className="px-5 pt-4">
          <div
            ref={timelineRef}
            className="relative h-8 cursor-pointer rounded-lg bg-slate-100"
            onMouseDown={handleTimelineMouseDown}
          >
            {/* 播放头 */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-slate-400"
              style={{ left: `${pct(currentTime)}%` }}
            />

            {/* trim 模式：选中区间 */}
            {mode === 'trim' && (
              <>
                <div
                  className="absolute top-0 bottom-0 bg-sky-200/60"
                  style={{ left: `${pct(trimIn)}%`, width: `${pct(trimOut - trimIn)}%` }}
                />
                {/* in 手柄 */}
                <div
                  className="absolute top-0 bottom-0 w-1.5 cursor-ew-resize rounded bg-sky-500"
                  style={{ left: `calc(${pct(trimIn)}% - 3px)` }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setDragging('in');
                  }}
                />
                {/* out 手柄 */}
                <div
                  className="absolute top-0 bottom-0 w-1.5 cursor-ew-resize rounded bg-sky-500"
                  style={{ left: `calc(${pct(trimOut)}% - 3px)` }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setDragging('out');
                  }}
                />
              </>
            )}

            {/* split 模式：分割线 */}
            {mode === 'split' && (
              <div
                className="absolute top-0 bottom-0 w-0.5 cursor-ew-resize bg-amber-500"
                style={{ left: `${pct(splitAt)}%` }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDragging('split');
                }}
              >
                <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-amber-500" />
              </div>
            )}

            {/* 时间刻度 */}
            <div className="absolute -bottom-4 left-0 text-[10px] text-slate-400">0:00</div>
            <div className="absolute -bottom-4 right-0 text-[10px] text-slate-400">
              {formatSec(duration)}
            </div>
          </div>
        </div>

        {/* 模式信息 */}
        <div className="px-5 pt-6">
          {mode === 'trim' && (
            <div className="flex items-center gap-4 text-xs text-slate-600">
              <span>
                起始 <span className="font-medium text-sky-600">{formatSec(trimIn)}</span>
              </span>
              <span>
                结束 <span className="font-medium text-sky-600">{formatSec(trimOut)}</span>
              </span>
              <span>
                选中时长{' '}
                <span className="font-medium text-sky-600">{formatSec(trimOut - trimIn)}</span>
              </span>
            </div>
          )}
          {mode === 'split' && (
            <div className="flex items-center gap-4 text-xs text-slate-600">
              <span>
                前段 <span className="font-medium text-amber-600">{formatSec(splitAt)}</span>
              </span>
              <span>
                后段{' '}
                <span className="font-medium text-amber-600">{formatSec(duration - splitAt)}</span>
              </span>
            </div>
          )}
          {mode === 'speed' && (
            <div className="flex items-center gap-2">
              {SPEED_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSpeed(s)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                    speed === s
                      ? 'bg-violet-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {s}x
                </button>
              ))}
              <span className="ml-2 text-xs text-slate-500">
                输出时长{' '}
                <span className="font-medium text-violet-600">
                  {formatSec(duration / speed)}
                </span>
              </span>
            </div>
          )}
        </div>

        {/* 底部操作 */}
        <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-3">
          {/* 模式切换 */}
          <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5">
            <button
              type="button"
              onClick={() => setMode('trim')}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                mode === 'trim' ? 'bg-white text-sky-600 shadow-sm' : 'text-slate-500'
              }`}
            >
              <Scissors className="h-3 w-3" />
              裁剪
            </button>
            <button
              type="button"
              onClick={() => setMode('split')}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                mode === 'split' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500'
              }`}
            >
              <Split className="h-3 w-3" />
              分割
            </button>
            <button
              type="button"
              onClick={() => setMode('speed')}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                mode === 'speed' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-500'
              }`}
            >
              <Gauge className="h-3 w-3" />
              变速
            </button>
          </div>

          <div className="flex-1" />

          {/* 移除片段（仅视频组场景） */}
          {clipIndex !== undefined && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={isProcessing}
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-rose-500 transition hover:bg-rose-50 disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" />
              移除
            </button>
          )}

          {/* 提交 */}
          {isProcessing ? (
            <div className="flex items-center gap-2 rounded-lg bg-sky-50 px-3 py-1.5 text-xs text-sky-600">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>
                {stageLabel(jobStatus?.stage)} {progress}%
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" />
              {submitting ? '提交中...' : '开始处理'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
